import { useEffect, useState, useMemo } from 'react';
import { supabase, Listing } from '@/lib/supabase';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  AreaChart, Area, PieChart, Pie, Cell, Legend, RadialBarChart, RadialBar
} from 'recharts';
import { 
  TrendingUp, Users, MessageSquare, Box, AlertTriangle, 
  ArrowUpRight, CheckCircle2, Search, Target, Award
} from 'lucide-react';
import { formatPrice } from '@/lib/utils';

// Paleta de colores profesional
const COLORS = {
  primary: '#3b82f6',   // Blue 500
  success: '#10b981',   // Emerald 500
  warning: '#f59e0b',   // Amber 500
  danger: '#ef4444',    // Red 500
  purple: '#8b5cf6',    // Violet 500
  slate: '#64748b',     // Slate 500
  pie: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ef4444']
};

export function AdminAnalytics() {
  const [listings, setListings] = useState<Listing[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const { data: listingsData } = await supabase.from('listings').select('*');
      const { data: messagesData } = await supabase.from('messages').select('id, created_at, storage_id');
      const { data: usersData } = await supabase.from('profiles').select('id, created_at, role');

      if (listingsData) setListings(listingsData);
      if (messagesData) setMessages(messagesData || []);
      if (usersData) setUsers(usersData || []);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  }

  // --- PROCESAMIENTO DE DATOS ---

  const kpis = useMemo(() => {
    const totalListings = listings.length;
    const activeListings = listings.filter(l => l.status === 'approved').length;
    const rentedListings = listings.filter(l => l.status === 'rented' || l.status === 'sold').length;
    const totalViews = listings.reduce((acc, curr) => acc + (curr.view_count || 0), 0);
    const totalMessages = messages.length;
    
    // Inmobiliarias (Usuarios con > 2 anuncios)
    const listingsByUser = listings.reduce((acc, curr) => {
      acc[curr.user_id] = (acc[curr.user_id] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const agenciesCount = Object.values(listingsByUser).filter(count => count > 2).length;

    return {
      totalListings,
      activeListings,
      rentedListings,
      totalViews,
      totalMessages,
      totalUsers: users.length,
      agenciesCount,
      conversionRate: totalViews > 0 ? ((totalMessages / totalViews) * 100).toFixed(2) : '0'
    };
  }, [listings, messages, users]);

  const growthData = useMemo(() => {
    const days = 30;
    const data = new Array(days).fill(0).map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (days - 1 - i));
      return { 
        date: d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }),
        rawDate: d.toISOString().split('T')[0],
        listings: 0,
        messages: 0
      };
    });

    listings.forEach(l => {
      const date = l.created_at.split('T')[0];
      const entry = data.find(d => d.rawDate === date);
      if (entry) entry.listings++;
    });

    messages.forEach(m => {
      const date = m.created_at.split('T')[0];
      const entry = data.find(d => d.rawDate === date);
      if (entry) entry.messages++;
    });

    return data;
  }, [listings, messages]);

  const segmentationData = useMemo(() => {
    // Por Tipo de Operación
    const byType = [
      { name: 'Alquiler', value: listings.filter(l => l.operation_type === 'rent').length },
      { name: 'Venta', value: listings.filter(l => l.operation_type === 'sale').length }
    ];

    // Por Tamaño
    const bySize = [
      { name: 'Pequeño (<3m²)', value: listings.filter(l => l.area_m2 < 3).length },
      { name: 'Mediano (3-8m²)', value: listings.filter(l => l.area_m2 >= 3 && l.area_m2 <= 8).length },
      { name: 'Grande (>8m²)', value: listings.filter(l => l.area_m2 > 8).length }
    ];

    // Características más comunes
    const featuresCount = {
      '24h': 0, 'Seguridad': 0, 'Acceso Calle': 0, 'Ascensor': 0, 'Carga': 0
    };
    listings.forEach(l => {
      const f = l.features || {};
      if (f.feat_24h) featuresCount['24h']++;
      if (f.feat_security) featuresCount['Seguridad']++;
      if (f.access_street) featuresCount['Acceso Calle']++;
      if (f.access_elevator) featuresCount['Ascensor']++;
      if (f.access_garage) featuresCount['Carga']++;
    });
    
    const byFeatures = Object.entries(featuresCount)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);

    return { byType, bySize, byFeatures };
  }, [listings]);

  const qualityMetrics = useMemo(() => {
    const total = listings.length || 1;
    const withImages = listings.filter(l => l.images && l.images.length > 0).length;
    const withDesc = listings.filter(l => l.description && l.description.length > 50).length;
    const withFeatures = listings.filter(l => l.features && Object.values(l.features).some(v => v)).length;
    
    return [
      { name: 'Con Fotos', value: Math.round((withImages / total) * 100), fill: COLORS.success },
      { name: 'Descripción Detallada', value: Math.round((withDesc / total) * 100), fill: COLORS.primary },
      { name: 'Características Completas', value: Math.round((withFeatures / total) * 100), fill: COLORS.purple }
    ];
  }, [listings]);

  const funnelData = useMemo(() => {
    const registered = users.length;
    const publishers = new Set(listings.map(l => l.user_id)).size;
    const successful = new Set(listings.filter(l => l.status === 'rented' || l.status === 'sold').map(l => l.user_id)).size;
    
    return [
      { name: 'Usuarios Registrados', value: registered },
      { name: 'Han Publicado', value: publishers },
      { name: 'Han Alquilado/Vendido', value: successful }
    ];
  }, [users, listings]);

  const cityPerformance = useMemo(() => {
    const cityMap = listings.reduce((acc, curr) => {
      const city = curr.city;
      if (!acc[city]) {
        acc[city] = { name: city, count: 0, views: 0, messages: 0, avgPrice: 0, totalPrice: 0 };
      }
      acc[city].count++;
      acc[city].views += (curr.view_count || 0);
      acc[city].totalPrice += curr.price;
      return acc;
    }, {} as Record<string, any>);

    messages.forEach(m => {
      const listing = listings.find(l => l.id === m.storage_id);
      if (listing && cityMap[listing.city]) {
        cityMap[listing.city].messages++;
      }
    });

    return Object.values(cityMap)
      .map(c => ({
        ...c,
        avgPrice: c.totalPrice / c.count,
        conversion: c.views > 0 ? ((c.messages / c.views) * 100).toFixed(1) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [listings, messages]);

  const alerts = useMemo(() => {
    const list = [];
    cityPerformance.forEach(c => {
      if (c.messages > 5 && c.count < 3) {
        list.push({ type: 'opportunity', msg: `Oportunidad en ${c.name}: Alta demanda, poca oferta.` });
      }
    });
    const noPhotos = listings.filter(l => !l.images || l.images.length === 0).length;
    if (noPhotos > 0) list.push({ type: 'warning', msg: `${noPhotos} anuncios sin fotos (baja calidad).` });
    
    return list;
  }, [listings, cityPerformance]);

  if (loading) return <div className="p-12 text-center text-slate-500 flex flex-col items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>Cargando inteligencia de mercado...</div>;

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      
      {/* 1. KPIs Principales */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KpiCard title="Trasteros Totales" value={kpis.totalListings} icon={Box} trend="+12%" color="blue" />
        <KpiCard title="Usuarios Registrados" value={kpis.totalUsers} icon={Users} trend="+5%" color="indigo" />
        <KpiCard title="Contactos Generados" value={kpis.totalMessages} icon={MessageSquare} trend="+18%" color="green" />
        <KpiCard title="Ratio Conversión" value={`${kpis.conversionRate}%`} icon={Target} trend="Stable" color="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* 2. Gráfico de Crecimiento */}
        <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" /> Crecimiento (30 días)
          </h3>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={growthData}>
                <defs>
                  <linearGradient id="colorListings" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.1}/>
                    <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.success} stopOpacity={0.1}/>
                    <stop offset="95%" stopColor={COLORS.success} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} />
                <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                <Legend />
                <Area type="monotone" dataKey="listings" name="Nuevos Anuncios" stroke={COLORS.primary} fillOpacity={1} fill="url(#colorListings)" strokeWidth={2} />
                <Area type="monotone" dataKey="messages" name="Contactos" stroke={COLORS.success} fillOpacity={1} fill="url(#colorMessages)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 3. Alertas y Resumen */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex flex-col">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Alertas Inteligentes
          </h3>
          <div className="flex-1 space-y-3 mb-6 overflow-y-auto max-h-[200px]">
            {alerts.length > 0 ? alerts.map((alert, idx) => (
              <div key={idx} className={`p-3 rounded-lg text-sm border flex gap-2 ${
                alert.type === 'opportunity' ? 'bg-green-50 border-green-100 text-green-800' : 'bg-amber-50 border-amber-100 text-amber-800'
              }`}>
                {alert.type === 'opportunity' ? <ArrowUpRight className="w-4 h-4 shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />}
                <span>{alert.msg}</span>
              </div>
            )) : (
              <p className="text-slate-500 text-sm italic">Todo en orden. ¡Buen trabajo!</p>
            )}
          </div>
          
          <div className="border-t border-slate-100 pt-4 mt-auto">
            <h4 className="font-semibold text-slate-900 mb-3 text-sm uppercase tracking-wider">Estado del Inventario</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 p-3 rounded-lg text-center">
                <span className="block text-2xl font-bold text-slate-900">{kpis.activeListings}</span>
                <span className="text-xs text-slate-500">Activos</span>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg text-center">
                <span className="block text-2xl font-bold text-slate-900">{kpis.rentedListings}</span>
                <span className="text-xs text-slate-500">Alquilados</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Segmentación y Calidad (NUEVO) */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        
        {/* Distribución por Tamaño */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Tamaño de Trasteros</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={segmentationData.bySize}
                  cx="50%" cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {segmentationData.bySize.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS.pie[index % COLORS.pie.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={36}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Calidad de Anuncios */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Award className="w-5 h-5 text-purple-500" /> Calidad del Contenido
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart innerRadius="30%" outerRadius="100%" barSize={20} data={qualityMetrics}>
                <RadialBar
                  label={{ position: 'insideStart', fill: '#fff' }}
                  background
                  dataKey="value"
                />
                <Legend iconSize={10} layout="vertical" verticalAlign="middle" wrapperStyle={{ right: 0 }} />
                <Tooltip />
              </RadialBarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Embudo de Éxito */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Embudo de Usuarios</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart layout="vertical" data={funnelData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" width={100} tick={{fontSize: 11}} />
                <Tooltip cursor={{fill: 'transparent'}} />
                <Bar dataKey="value" fill={COLORS.primary} radius={[0, 4, 4, 0]} barSize={30}>
                    {funnelData.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={[COLORS.slate, COLORS.primary, COLORS.success][index]} />
                    ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 5. Tabla de Rendimiento por Ciudad */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <Search className="w-5 h-5 text-slate-400" /> Rendimiento por Ciudad (Top 10)
            </h3>
        </div>
        <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-500 font-medium">
                    <tr>
                        <th className="px-6 py-3">Ciudad</th>
                        <th className="px-6 py-3 text-center">Oferta</th>
                        <th className="px-6 py-3 text-center">Vistas</th>
                        <th className="px-6 py-3 text-center">Contactos</th>
                        <th className="px-6 py-3 text-center">Conversión</th>
                        <th className="px-6 py-3 text-right">Precio Medio</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {cityPerformance.map((city) => (
                        <tr key={city.name} className="hover:bg-slate-50 transition-colors">
                            <td className="px-6 py-3 font-medium text-slate-900">{city.name}</td>
                            <td className="px-6 py-3 text-center">
                                <span className="inline-block px-2 py-1 bg-slate-100 rounded-md text-slate-700 font-bold">
                                    {city.count}
                                </span>
                            </td>
                            <td className="px-6 py-3 text-center text-slate-600">{city.views}</td>
                            <td className="px-6 py-3 text-center">
                                <span className={`font-bold ${city.messages > 0 ? 'text-blue-600' : 'text-slate-300'}`}>{city.messages}</span>
                            </td>
                            <td className="px-6 py-3 text-center">
                                <div className="flex items-center justify-center gap-2">
                                    <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                        <div 
                                            className={`h-full rounded-full ${Number(city.conversion) > 5 ? 'bg-green-500' : 'bg-slate-400'}`} 
                                            style={{ width: `${Math.min(Number(city.conversion), 100)}%` }}
                                        ></div>
                                    </div>
                                    <span className="text-xs font-medium">{city.conversion}%</span>
                                </div>
                            </td>
                            <td className="px-6 py-3 text-right font-medium text-slate-700">
                                {formatPrice(city.avgPrice)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ title, value, icon: Icon, trend, color }: any) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
  };

  return (
    <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${(colorClasses as any)[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
        <span className={`text-xs font-medium px-2 py-1 rounded-full ${trend.includes('+') ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
          {trend}
        </span>
      </div>
      <p className="text-slate-500 text-sm font-medium">{title}</p>
      <h3 className="text-2xl font-bold text-slate-900 mt-1">{value}</h3>
    </div>
  );
}
